import { createFileRoute, Link, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { LayoutDashboard, Package, Images, User, Film, MessagesSquare, PanelBottom, Navigation, Users, Tag, Megaphone, ArrowLeft, Search } from "lucide-react";

export const Route = createFileRoute("/admin")({
  head: () => ({ meta: [{ title: "Admin — Jul's Skin" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: AdminLayout,
});

function AdminLayout() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) navigate({ to: "/auth" });
    else if (!isAdmin) navigate({ to: "/account" });
  }, [user, isAdmin, loading, navigate]);

  if (loading || !user || !isAdmin) return null;

  const tabs = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard },
    { to: "/admin/products", label: "Products", icon: Package },
    { to: "/admin/categories", label: "Categories", icon: Tag },
    { to: "/admin/nav", label: "Navigation", icon: Navigation },
    { to: "/admin/announcements", label: "Announcements", icon: Megaphone },
    
    { to: "/admin/whatsapp-orders", label: "WhatsApp Orders", icon: MessagesSquare },
    { to: "/admin/slides", label: "Slides", icon: Images },
    { to: "/admin/social", label: "Social Videos", icon: Film },
    { to: "/admin/footer", label: "Footer", icon: PanelBottom },
    { to: "/admin/seo", label: "Google Rank", icon: Search },
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/account", label: "Account", icon: User },
  ] as const;

  return (
    <div className="mx-auto max-w-6xl px-6 py-10">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to shop
      </Link>
      <h1 className="display text-3xl mb-6">Admin Panel</h1>
      <nav className="flex flex-wrap gap-2 border-b border-border mb-8">
        {tabs.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            activeOptions={{ exact: true }}
            className="flex items-center gap-2 px-4 py-2 text-sm border-b-2 border-transparent hover:text-foreground text-muted-foreground [&.active]:border-foreground [&.active]:text-foreground"
            activeProps={{ className: "active" }}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </Link>
        ))}
      </nav>
      <Outlet />
    </div>
  );
}
